<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">OCCUPANCY WORKSHEET > MOVE INS / OUTS TAB</h1>
    <span class="banner py-3 mb-4">
      <div>This tab will allow you to calculate the number of Move Ins and Move Outs, which will contribute to Vacancy Loss.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Summary Section</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>The numbers showing in the table at the top are a result of the calculations below.</div>
          <div><strong>Lease Expiration Limits</strong> - This will display the limits that have been setup in Admin > Properties > Within your property > Lease Expiration Settings > Lease Expiration Limits.</div>
          <div><strong>Expiring Leases</strong> - The total number of leases whose Lease End Date falls in this month. This will match the count from an Expiring Leases report for that month (excluding Former, and Evicted leases)</div>
          <ul>
            <li>Historical - This will match the count from an Expiring Leases report for that month (include all lease types)</li>
          </ul>
          <div><strong>Projected Renewal Rate % </strong>-</div>
          <ul>
            <li>If viewing Property Level: displays the same number from the calculation table below.</li>
            <li>If viewing Unit Type level: displays the Projected Renewal Rate % from each Unit Type averaged.</li>
            <li>Historical: This will be the actual renewal rate· You can verify this number by running an Expiring Lease report for the month (select All lease statuses) and dividing the number of expiring leases by the number of leases with status R* (Renewed).</li>
          </ul>
          <div><strong>Total Move Outs</strong> - Addition of each Unit Type's move outs</div>
          <div><strong>Total Move Ins</strong> - Addition of each Unit Type's move ins</div>
          <div><strong>Ending Occupied Units</strong> - Prior Occupied Units + Move Ins - Move Outs</div>
          <div><strong>Ending Vacant Units</strong> - Prior Vacant Units - Move Ins + Move Outs</div>
          <div><strong>Ending Model/Admin/Down Units</strong> - The number of Model/Admin/Down units from the MODEL/ADMIN/DOWN tab.</div>
          <div><strong>Total Units</strong> - Total Unit count (the sum of the Ending Occupied Units, Ending Vacant Units and Ending Model/Admin/Down Units.</div>
          <div><strong>Ending Vacancy Loss (Excluding Model/Admin/Down)</strong> - Ending Vacant Units multiplied by Gross Potential Rent.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Goal Section</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>You will be able to enter your desired <strong>Property Renewal Goal %</strong> (Example: 50%) and your <strong>Property Occupancy Goal %</strong> (Example: 97%) for each month.</div>
          <div>Even if you have not selected to view historical data, these two rows will always provide last year's information to assist you in your decisions.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>You will have an option to view and calculate your Renewals, Move Outs and Move Ins at either the Property level or at the Unit Type level.</div>
          <div>Clear and Reset will clear out all data that has been entered on this tab.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculation Table - Property Level</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Lease Expiration Limits</strong> - matches the numbers located in Admin > Properties > Within the property > Lease Expiration Limits for each Month of the year. These numbers are not used in any calculations and are provided to assist you in making your decisions.</div>
          <div><strong>Expiring Leases</strong> - will initially show the number of Expiring leases for that unit type in that month. You will be able to edit this information, which will be particularly helpful for the last few months in your budgeting year as they may initially be blank.</div>
          <div><strong>Projected # Renewed</strong> - Expiring Leases x Unit Type Renewal Goal % will show initially but may be edited.</div>
          <div><strong>Projected Renewal %</strong> - Is Expiring Leases / Projected # Renewed. This is the actual Renewal % after making any changes above.</div>
          <div><strong>PROJECTED MOVE-OUTS</strong> - Initially will be Expiring Leases x (100 - Unit Type Renewal Goal %). This may be edited.</div>
          <div><strong>Estimated MTM Leases</strong> - Initially this will be blank. You can enter the number of leases that you estimate will be on Month-To-Month status.</div>
          <div><strong>Estimated MTM Move Outs</strong> - Initially this will be blank. You can enter the number of those MTM leases that you expect will move out each month.</div>
          <div><strong>Estimated Skips/Evictions</strong> - Initially this will be blank. You can enter the number of skips and evictions that you anticipate for each month.</div>
          <div><strong>ENDING MOVE-OUTS</strong> - will be PROJECTED MOVE-OUTS + Estimated MTM Move Outs + Estimated Skips/Evictions.</div>
          <div><strong>Starting Vacant Units</strong> - for the first month of the budget year, this will pull from the Settings page. It will be calculated for each month afterwards.</div>
          <div><strong>Vacants from Projected MO's</strong> - This displays your ENDING MOVE-OUT count.</div>
          <div><strong>Total Units available to lease</strong> - Starting Vacant Units + Vacants from Projected MO's. This is the number of units that could possibly move in.</div>
          <div><strong>ENDING MOVE-INS</strong> - The system will do a calculation to determine the number of Move Ins that will result closest to the <strong>Property Occupancy Goal %</strong>. It may not be exact as in some cases it would require part of a Move In to exactly match the Goal %.</div>
          <div><strong>Calculation Details for ENDING MOVE-INS:</strong></div>
          <ul>
            <li>Total Rentable Units = Total Units - Model/Admin/Down Units</li>
            <li>Goal Occupied Units = Property Occupancy Goal % * Total Rentable Units (rounded)</li>
            <li>Current Occupied Units (after Move Outs) = Total Rentable Units - Total Units Available to Lease</li>
            <li>ENDING MOVE-INS = Goal Occupied Units - Current Occupied Units (after Move Outs)</li>
          </ul>
          <div>
            Note: If the the Current Occupied Units (after Move Outs) is greater than the Goal Occupied Units,
            then the number of Move-Ins will be zero.
          </div>
          <div><strong>Ending Vacancy Loss (excluding Model/Admin/Down)</strong> - the number of Ending Vacant Units multiplied by the Gross Potential Rent.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculation Table: Unit Type Level</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>When the Unit Types are collapsed, the Unit Type Physical Occupancy % will display.</div>
          <div>Once you open the Unit type, you will see the following rows:</div>
          <div><strong>Physical Occupancy</strong> - Your first month will take the ending previous month's Total Units divided by Occupied Units. Each month afterwards will take Total Units divided by the budgeted Occupied Units.</div>
          <div><strong>Lease Expiration Limits</strong> - matches the numbers located in Admin > Properties > Within the property > Lease Expiration Limits for each Month of the year. These numbers are not used for any calculations but are provided to assist you in making your decisions.</div>
          <div><strong>Expiring Leases</strong> - will initially show the number of Expiring leases for that unit type in that month. You will be able to edit this information, which will be particularly helpful at the last few months in your budgeting year as they may initially be blank.</div>
          <div><strong>Projected # Renewed</strong> - Property Renewal Goal % multiplied by Expiring Leases will show initially but may be edited.</div>
          <div><strong>Projected Renewal %</strong> - Will show Expiring Leases / Projected # Renewed. This is the actual Renewal % after making any changes above.</div>
          <div><strong>PROJECTED MOVE-OUTS</strong> - Initially will be Expiring Leases x (100 - Unit Type Renewal Goal %). This may be edited.</div>
          <div><strong>Estimated MTM Leases</strong> - Initially this will be blank. You can enter the number of leases that you estimate will be on Month-To-Month status.</div>
          <div><strong>Estimated MTM Move Outs</strong> - Initially this will be blank. You can enter the number of those MTM leases that you expect will move out each month.</div>
          <div><strong>Estimated Skips/Evictions</strong> - Initially this will be blank. You can enter the number of skips and evictions that you anticipate for each month.</div>
          <div><strong>ENDING MOVE-OUTS</strong> - will be PROJECTED MOVE-OUTS + Estimated MTM Move Outs + Estimated Skips/Evictions.</div>
          <div><strong>Starting Vacant Units</strong> - for the first month of the budget year, this will pull from the Settings page. It will be calculated for each month afterwards.</div>
          <div><strong>Vacants from Projected MO's</strong> - This displays your ENDING MOVE-OUT count.</div>
          <div><strong>Total Units available to lease</strong> - Starting Vacant Units + Vacants from Projected MO's. This is the number of units that could possibly move in.</div>
          <div><strong>ENDING MOVE-INS</strong> - The system will do a calculation to determine the number of Move Ins that will result closest to the Property Occupancy Goal %. It may not be exact as in some cases it would require part of a Move In to exactly match the Goal %.</div>
          <div><strong>Calculation Details for ENDING MOVE-INS:</strong></div>
          <ul>
            <li>Total Rentable Units = Total Units - Model/Admin/Down Units</li>
            <li>Goal Occupied Units = Property Occupancy Goal % * Total Rentable Units (rounded)</li>
            <li>Current Occupied Units (after Move Outs) = Total Rentable Units - Total Units Available to Lease</li>
            <li>ENDING MOVE-INS = Goal Occupied Units - Current Occupied Units (after Move Outs)</li>
          </ul>
          <div>
            Note: If the the Current Occupied Units (after Move Outs) is greater than the Goal Occupied Units,
            then the number of Move-Ins will be zero.
          </div>
          <div><strong>Ending Vacancy Loss (excluding Model/Admin/Down)</strong> - the number of Ending Vacant Units multiplied by the Gross Potential Rent.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons & Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Within each of the Occupancy tabs you will have additional options on the right-side of the screen.</div>
          <img src="../../assets/help/a.png" />
          <div>
            <img class="small" src="../../assets/help/b.png" />
            <span>This icon will bring up a help screen specific to this page</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/c.png" />
            <span>This icon will open the Comments and Flags screen which will allow you to add a general comment, create a flagged comment, or reply to an existing comment. If it is empty in the middle, there are no comments or flags.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/d.png" />
            <span>If it has changed to a solid blue, then comments have been entered.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/e.png" />
            <span>If it has changed to an orange flag, then an unresolved flag or issue exists. Someone will need to click into the Comments and Flags screen and select Resolve the outstanding issue. Once resolved, the icon will change back to blue.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/f.png" />
            <span>Clicking this ellipsis will bring up a new menu of options.</span>
          </div>
          <br />
          <div><strong>Add Flag or Comment</strong> will bring up the same screen as the center icon allowing you to add a comment, a flagged comment or resolve a comment</div>
          <div><strong>Clear & Reset</strong> will clear out any data that has been added and reset this tab back to the default values when the page was first loaded. This only applies to the specific tab you are on. It will also popup a message to explain what is about to happen and to allow you to confirm or cancel.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Occupancy Move In Move Out Tab')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'OccupancyMoveInOutHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
